import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TaskScheduleToken, EnumToken } from '@sleed/bizcommerce-api';
import { taskSchedulesApi } from '~/lib/api/client';
import { formToToken, type TaskScheduleFormValues } from '~/lib/schemas/task-schedule';

export const taskScheduleKeys = {
	all: ['task-schedules'] as const,
	list: () => [...taskScheduleKeys.all, 'list'] as const,
	verbosities: () => [...taskScheduleKeys.all, 'verbosities'] as const
};

export function useTaskSchedulesList() {
	return useQuery<TaskScheduleToken[]>({
		queryKey: taskScheduleKeys.list(),
		queryFn: async () => (await taskSchedulesApi.getAll()).data.items ?? []
	});
}

/** Log-verbosity lookup ({id, nm}) for the edit dropdown. */
export function useTaskLogVerbosities() {
	return useQuery<EnumToken[]>({
		queryKey: taskScheduleKeys.verbosities(),
		queryFn: async () => (await taskSchedulesApi.getLogVerbosities()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useTaskScheduleMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: taskScheduleKeys.list() });
	const create = useMutation({
		mutationFn: async (form: TaskScheduleFormValues) => (await taskSchedulesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: TaskScheduleFormValues) => (await taskSchedulesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await taskSchedulesApi.remove(id)).data, onSuccess: invalidate });
	/** Queue an immediate run by setting the `run` flag (the scheduler picks it up). */
	const run = useMutation({
		mutationFn: async (form: TaskScheduleFormValues) => (await taskSchedulesApi.update(formToToken({ ...form, run: true }))).data,
		onSuccess: invalidate
	});
	return { create, update, remove, run };
}
